import { ChangeDetectionStrategy, Component, effect, ElementRef, inject, input, OnInit, viewChild, ViewEncapsulation } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ItemsRatesService } from "@app/cem/project/items/items-rates.service";
import { ItemRateManagementService } from "@app/cem/project/services/item-rate-management.service";
import { ErrorService } from "@app/common/services/error.service";
import { PxdAgGridCheckboxRenderer, PxdAgGridCheckboxRendererReadOnly } from "@app/shared/aggrid/pxd-ag-grid-cell-renderers";
import { PxdAgDatePickerEditorComponent } from "@app/shared/aggrid/pxd-ag-grid-datepicker-editor";
import { PxdAgGridNumericEditorComponent } from "@app/shared/aggrid/pxd-ag-grid-numeric-editor";
import { PxdAgEditGridDefaultColumnDef, PxdAgGridColumnTypes, PxdAgGridExcelStyles, PxdAgGridOptions, PxdAgGridSideBarOptions, PxdAgGridStatusBarOptions, PXDDefaultExcelExportParams } from '@app/shared/aggrid/pxd-ag-grid-options';
import { PxdAgGridSelectEditorComponent } from "@app/shared/aggrid/pxd-ag-grid-select-editor";
import { kendoIcons } from '@app/shared/library/kendo-icons';
import { IItemDto, IItemRateDto } from "@app/shared/models/dtos/dtos";
import { ItemRateExtract } from "@app/shared/models/dtos/item-rate-extract";
import { IProjectFieldDto } from "@app/shared/models/dtos/project-field";
import { IProjectFieldToFieldXref } from "@app/shared/models/dtos/project-field-to-field-xref";
import { IRateValue } from "@app/shared/models/dtos/rate-value";
import { ItemRateDtoValidator, RateValueNoLookupValidator } from "@app/shared/models/validators/item-rate-validator";
import { ButtonDirective } from '@progress/kendo-angular-buttons';
import { DialogActionsComponent, DialogComponent } from '@progress/kendo-angular-dialog';
import { DropDownFilterSettings, DropDownListModule } from "@progress/kendo-angular-dropdowns";
import { PopupComponent } from '@progress/kendo-angular-popup';
import { AgGridAngular } from "ag-grid-angular";
import { CellRange, ColDef, DefaultMenuItem, GetContextMenuItemsParams, GridApi, IRowNode, MenuItemDef, RangeSelectionChangedEvent, RowNode } from 'ag-grid-community';
import { Observable, Subject, Subscription } from "rxjs";
import { takeUntil } from "rxjs/operators";

@Component({
  selector: 'app-items-details-rates',
  templateUrl: './items-details-rates.component.html',
  styleUrls: ['./items-details-rates.component.scss'],
  encapsulation: ViewEncapsulation.None,
  host: {
    '(keydown)': 'keydown($event)',
    '(document:click)': 'documentClick($event)',
  },
  imports: [
    AgGridAngular,
    ButtonDirective,
    DialogActionsComponent,
    DialogComponent,
    DropDownListModule,
    FormsModule,
    PopupComponent,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    ItemRateManagementService,
    ItemsRatesService,
  ],
})
export class ItemsDetailsRatesComponent implements OnInit {
  private itemRateManagementService = inject(ItemRateManagementService);
  private itemsRatesService = inject(ItemsRatesService);
  private errorService = inject(ErrorService);

  public icons = kendoIcons;
  IItemDto: IItemDto;
  public IItemRateDtos: Array<IItemRateDto> = new Array<IItemRateDto>();
  public ValidationErrors: Map<string, Array<string>> = new Map<string, Array<string>>();
  public validationMessage: string;
  private gridApi: GridApi;
  private firstLoad = true;
  public showAddColumn = false;
  public showRemoveColumn = false;
  public editMode: boolean = false;
  public loading: boolean = true;
  private readonly agGrid = viewChild(AgGridAngular);
  newItemId = 0;
  public editedEntities: Map<number, IItemRateDto> = new Map<any, IItemRateDto>();
  private itemRateDtoValidator = new ItemRateDtoValidator();
  private rateValueNoLookupValidator = new RateValueNoLookupValidator();
  unsubscribe$: Subject<boolean> = new Subject();
  public IProjectFieldDtos: Array<IProjectFieldDto> = new Array<IProjectFieldDto>();
  public addNewIProjectFieldDto: IProjectFieldDto;
  public removeIProjectFieldDto: IProjectFieldDto;
  public projectFieldLookups = new Array<IProjectFieldDto>();
  public dialogOpened = false;
  private refreshRatesSubscription: Subscription;
  public activeEditing: boolean = false;
  public currentRowNode: RowNode = null;
  public rowClassRules = { 'duplicate-row': function (params) { return params && params.data && params.data.ItemRateId <= 0 } };

  readonly refreshRates = input<Observable<void>>(undefined);
  readonly itemId = input<number>(undefined);

  public gridOptions: PxdAgGridOptions = {
    enableCharts: true,
    rowSelection: { mode: 'multiRow' },
    cellSelection: true,
    rowGroupPanelShow: 'always',
    editType: 'fullRow',
    defaultColDef: PxdAgEditGridDefaultColumnDef,
    statusBar: PxdAgGridStatusBarOptions,
    sideBar: PxdAgGridSideBarOptions,
    columnTypes: PxdAgGridColumnTypes,
    enableGroupEdit: true,
    columnDefs: this.getColumnDefs(),
    excelStyles: this.getExcelStyles(),
    defaultExcelExportParams: PXDDefaultExcelExportParams
  };

  // keeps track of range selections for use when duplicating
  private lastRangeSelected: IRowNode[] = [];
  onRangeSelectionChanged(event: RangeSelectionChangedEvent) {
    if (!event.finished) { return }
    let cellRanges: CellRange[] = this.gridApi.getCellRanges();
    if (cellRanges.length === 1) {
      if (cellRanges[0].startRow === cellRanges[0].endRow) {
        this.lastRangeSelected = [];
        return;
      }
    }
    let rangeSelectedRowNodes: IRowNode[] = [];
    cellRanges.forEach(cr => {
      var startRow = Math.min(
        cr.startRow!.rowIndex,
        cr.endRow!.rowIndex
      );
      var endRow = Math.max(
        cr.startRow!.rowIndex,
        cr.endRow!.rowIndex);
      for (var rowIndex = startRow; rowIndex <= endRow; rowIndex++) {
        let rowNode = this.gridApi.getDisplayedRowAtIndex(rowIndex)
        rangeSelectedRowNodes.push(rowNode);
      }
    });
    this.lastRangeSelected = []
    this.lastRangeSelected = rangeSelectedRowNodes;
  }

  public getContextMenuItems = (params: GetContextMenuItemsParams):
    (DefaultMenuItem | MenuItemDef)[] | Promise<(DefaultMenuItem | MenuItemDef)[]> => {
    const result: (DefaultMenuItem | MenuItemDef)[] = [
      'copy',
      'copyWithHeaders',
      'paste',
      {
        name: 'Duplicate',
        shortcut: 'Ctrl+Shift+D',
        action: () => { this.duplicateRows(); }
      },
      'export',
      'csvExport',
      'excelExport',
    ];
    return result;
  };

  duplicateRows() {
    let selectedNodes: IRowNode[] = this.gridApi.getSelectedNodes();
    if (this.lastRangeSelected.length > 0) {
      selectedNodes.push.apply(selectedNodes, this.lastRangeSelected)
    }
    let rowNodes: Set<IRowNode> = new Set();
    selectedNodes.forEach(x => {
      if (x.group) {
        let groupedChildren = x.childrenAfterFilter.filter(y => !y.group);
        groupedChildren.forEach(rn => rowNodes.add(rn));
      }
      else {
        rowNodes.add(x);
      }
    });
    if (!rowNodes.size) {
      this.errorService.display("Nothing Selected");
      return;
    }
    rowNodes.forEach(node => {
      let newOne = {} as IItemRateDto;
      let newOneId = this.newItemId--;
      Object.assign(newOne, node.data);
      newOne.RateValues = [];
      node.data.RateValues.forEach(rv => {
        let newRateValue = {} as IRateValue;
        Object.assign(newRateValue, rv);

        newRateValue.ItemRateId = newOneId
        newRateValue.ProjectFieldRateValueId = 0;
        newOne.RateValues.push(newRateValue);
      });
      newOne.ItemRateId = newOneId;
      this.IItemRateDtos.push(newOne);
      this.editedEntities.set(newOne.ItemRateId, newOne);
      this.agGrid().api.applyTransaction({
        add: [newOne],
        addIndex: node.rowIndex + 1
      });
    })
    this.editMode = true;
  }

  onCellKeyDown(e) {
    if (e.event) {
      let ctrlPressed = e.event.ctrlKey
      let shiftPressed = e.event.shiftKey;
      let keyPressed = (e.event as KeyboardEvent).key;
      if (ctrlPressed && shiftPressed && keyPressed === "D") {
        this.duplicateRows();
      }
    }
  }

  getExcelStyles() {
    return PxdAgGridExcelStyles;
  }

  getColumnDefs() {
    return [
      {
        field: 'ItemRateId',
        headerName: 'Item Rate ID',
        editable: false,
        width: 60
      },
      {
        field: 'EffectiveDate',
        headerName: 'Effective Date',
        type: ['dateColumn'],
        editable: true,
        cellClass: 'ExcelDate',   //matches the excel style 'id' so that it uses mm/dd/yy on excel exports
        cellEditorSelector: params => {
          return {
            component: PxdAgDatePickerEditorComponent,
            params: {
              value: params.data.EffectiveDate,
              popup: false
            }
          }
        },
        filter: 'agDateColumnFilter',
        // add extra parameters for the date filter
        filterParams: {
          includeBlanksInEquals: false,
          includeBlanksInGreaterThan: false,
          includeBlanksInLessThan: false
        }
      },
      {
        field: 'Rate',
        editable: true,
        headerName: 'Rate',
        type: ['numberColumn'],
        cellEditorSelector: params => {
          return { component: PxdAgGridNumericEditorComponent };
        },
        cellEditorParams: {
          decimals: 5, format: "n5"
        }
      },
      {
        field: 'IsActive',
        editable: true,
        headerName: 'Is Active',
        cellRenderer: PxdAgGridCheckboxRendererReadOnly,
        cellEditorSelector: params => {
          return { component: PxdAgGridCheckboxRenderer };
        },
        width: 100
      }
    ]
  }

  constructor() {
    effect(() => {
      let value = this.itemId();
      this.itemsRatesService.setItemDtoById(value);
    });
   }

  ngOnInit() {
    this.initNewIProjectFieldDto();
    this.refreshRatesSubscription = this.refreshRates().subscribe(() => this.initNewIProjectFieldDto());
  }

  ngOnDestroy() {
    this.refreshRatesSubscription.unsubscribe();
    this.unsubscribe$.next(true);
    this.unsubscribe$.complete();
  }

  initNewIProjectFieldDto() {
    this.addNewIProjectFieldDto = {
      ProjectFieldId: 0
      , FieldName: ''
      , DefaultValue: ''
      , JavaScriptType: ''
      , TextField: ''
      , LookupValues: null
      , ValueField: ''
      , PropertyPath: ''
      , PropertyPathNames: ''
    };
    this.removeIProjectFieldDto = {
      ProjectFieldId: 0
      , FieldName: ''
      , DefaultValue: ''
      , JavaScriptType: ''
      , TextField: ''
      , LookupValues: null
      , ValueField: ''
      , PropertyPath: ''
      , PropertyPathNames: ''
    };
  }

  async loadGrid() {
    this.gridApi.setGridOption('loading', true);
    this.initNewIProjectFieldDto();
    this.gridApi.resetColumnState();
    this.loading = true;
    this.editMode = false;
    this.editedEntities = new Map<number, IItemRateDto>();
    this.ValidationErrors = new Map<string, Array<string>>();
    this.newItemId = 0;

    if (this.baseColumnDefs == null) {
      this.baseColumnDefs = this.gridApi.getColumnDefs();
      this.gridApi.setGridOption('columnDefs', this.baseColumnDefs);
      this.projectFieldLookups = new Array<IProjectFieldDto>();
    } else {
      try {
        this.gridApi.stopEditing(true);
        this.gridApi.setGridOption('columnDefs', this.baseColumnDefs);
      } catch (e) {
        this.errorService.error("Error while loading column definitions", e);
      }
      this.projectFieldLookups = new Array<IProjectFieldDto>();
    }
    let projectFields = await this.itemRateManagementService.getItemProjectFieldIds(this.itemId(), true);
    let itemRates = await this.itemRateManagementService.getItemRateProjectFields(this.itemId());
    projectFields.forEach(projectField => {
      this.addColumnDefinition(projectField);
      itemRates.forEach((itemRate: IItemRateDto) => {
        let itemRateValues = itemRate.RateValues.filter(obj => obj.ProjectFieldId === projectField.ProjectFieldId);
        if (itemRateValues && itemRateValues.length > 0) {
          itemRate['Field_' + projectField.ProjectFieldId] = itemRateValues[0].FieldValue;
        }
        else {
          itemRate['Field_' + projectField.ProjectFieldId] = null;
        }
      });
    });
    this.IItemRateDtos = itemRates;
    this.addAuditColumns();
    this.gridApi.setGridOption('rowData', this.IItemRateDtos);
    if (this.IItemRateDtos.length) { //AgGrid will freeze if filter is applied to empty data
      await this.gridApi.setColumnFilterModel('IsActive', { values: ["true"] });
    }
    this.gridApi.onFilterChanged();
    if (true || this.firstLoad) // TODO: this doesn't quite work, we'll enhance this later
    {
      this.autoSizeAll(false);
      this.firstLoad = false;  //do not auto size after the first load, in case the user manually set columns
    }
    this.loading = false;
    this.gridApi.setGridOption('loading', false);
  }

  autoSizeAll(skipHeader: boolean) {
    const allColumnIds: string[] = [];
    this.agGrid().api.getAllGridColumns()!.forEach((column) => {
      allColumnIds.push(column.getId());
    });
    this.agGrid().api.autoSizeColumns(allColumnIds, skipHeader);
  }

  onGridReady(params) {
    this.gridApi = params.api;

    this.itemsRatesService.getItemDto().pipe(takeUntil(this.unsubscribe$)).subscribe(response => {
      this.IItemDto = response.Result;

      if (this.IItemDto.FunctionId !== null) {
        this.itemRateManagementService.getProjectFields(this.IItemDto.FunctionId).subscribe(projectFieldResponse => {
          this.IProjectFieldDtos = projectFieldResponse.sort((a, b) => a.FieldName.toLowerCase().localeCompare(b.FieldName.toLowerCase()));
          this.loadGrid();
        });
      } else if (this.IItemDto.ItemId !== 0) {
        this.loadGrid();
      }
    });
  }

  public onCellEditingStarted(e) {
    this.currentRowNode = e.node;
    this.activeEditing = true;
  }

  public onCellEditingStopped(e) {
    this.currentRowNode = null;
    this.activeEditing = false;
  }

  public cancelChanges() {
    this.gridApi.stopEditing(true);
    this.loadGrid();
  }

  addNewItem() {
    let dta = <IItemRateDto>{
      ItemRateId: this.newItemId--
      , ItemId: this.IItemDto.ItemId
      , IsActive: true
    };

    let rateValues = new Array<IRateValue>();
    this.projectFieldLookups.forEach(value => {
      let rv = <IRateValue>{
        ItemRateId: dta.ItemRateId
        , ProjectFieldId: value.ProjectFieldId
        , FieldValue: null
      };
      rateValues.push(rv);
    });
    dta.RateValues = rateValues;
    this.IItemRateDtos.push(dta);
    this.editMode = true;
    this.agGrid().api.applyTransaction({
      add: [dta], addIndex: 0
    });
    this.agGrid().api.startEditingCell({ rowIndex: 0, colKey: 'EffectiveDate' });
  }

  public transactAnyCurrentEditing() {
    if (this.activeEditing) {
      this.gridApi.stopEditing();
      this.editMode = true;
      this.editedEntities.set(this.currentRowNode.data.ItemRateId, this.currentRowNode.data);
      this.currentRowNode = null;
      this.activeEditing = false;
    }
  }

  private validateRateValue(projectFieldDto: IProjectFieldDto, rateValue: IRateValue) {
    if (projectFieldDto.TextField != null || projectFieldDto.ValueField != null) {
      return;
    }
    let valResult = this.rateValueNoLookupValidator.validate(rateValue);
    const validationValues = Object.values(valResult);

    if (validationValues.length > 0) {
      let messages = new Array<string>();

      validationValues.forEach(message => {
        messages.push(message as string)
      });
      this.ValidationErrors.set('Validation Error', messages);
    }
  }

  public async saveChanges() {
    this.transactAnyCurrentEditing();
    this.loading = true;
    if (this.editedEntities.size === 0) {
      this.loading = false;
      return;
    }

    this.ValidationErrors = new Map<string, Array<string>>();
    let array = Array.from(this.editedEntities.values());

    array.forEach(ir => {
      let obj = this.itemRateDtoValidator.validate(ir);
      const values = Object.values(obj);
      if (values.length > 0) {
        let messages = new Array<string>();

        values.forEach(message => {
          messages.push(message as string)
        });
        this.ValidationErrors.set('Validation Error', messages);
      }
      else {
        //TODO: Reverse the forEach
        this.projectFieldLookups.forEach(value => {
          let rateValues = ir.RateValues.filter(obj => obj.ProjectFieldId === value.ProjectFieldId);
          if (rateValues && rateValues.length > 0) {
            rateValues[0].FieldValue = ir['Field_' + value.ProjectFieldId];
          }
          else {
            let rv = <IRateValue>{
              ItemRateId: ir.ItemRateId
              , ProjectFieldId: value.ProjectFieldId
              , FieldValue: ir['Field_' + value.ProjectFieldId] ?? ir[value.ProjectFieldId] //in the case of new columns, the 'Field_' prefix is not yet there
            };
            ir.RateValues.push(rv);
          }

          ir.RateValues.forEach(rateValue => {
            try {
              this.validateRateValue(value, rateValue);
            }
            catch (err) {
              throw new Error("Error in ItemRate Validation: " + err.Message);
            }
          });
        });
      }
    });

    if (this.ValidationErrors.size > 0) {
      this.validationMessage = Array.from(this.ValidationErrors.values()).join(',');
      this.loading = false;
      return;
    }
    try {
      this.gridApi.setGridOption('loading', true);
      await this.itemRateManagementService.postItemRateProjectFields(array);
      this.loadGrid();
    }
    catch (error) {
      if (error.status == 422) {
        let groupedItemRates = this.handleValidationResults(error.error.itemRateExtracts);
        groupedItemRates.forEach((v, k) => {
          let itemRateIds = new Array();
          v.forEach(x => {
            itemRateIds.push(x.ItemRateId);
          })
          this.ValidationErrors.set(`${k}: ${v[0].Issue} (${v.length.toString()})`, [itemRateIds.join(", ")]);
        });
      }
      else {
        this.errorService.error(error.error.ExceptionType, error.error);
      }
      this.loading = false;
    }
    this.gridApi.setGridOption('loading', false);
  }

  private handleValidationResults(itemRateExtracts: ItemRateExtract[]) {
    let groupedItemRates: Map<string, ItemRateExtract[]> = new Map();
    itemRateExtracts.forEach(x => {
      let key = x.GroupNum.toString();
      const collection = groupedItemRates.get(key);
      if (!collection) {
        groupedItemRates.set(key, [x]);
      } else {
        collection.push(x);
      }
    });
    return groupedItemRates;
  }

  onCellValueChanged(e) {
    this.trackChanges(e);
  }

  trackChanges(e) {
    if ((e.colDef &&
      e.colDef.field == "EffectiveDate" &&
      e.newValue &&
      e.oldValue &&
      e.oldValue.getTime() == e.newValue.getTime())
      || (
        e.newValue &&
        e.oldValue &&
        e.oldValue == e.newValue)) {
      return;
    }
    this.editMode = true;
    e.data.Rate = +(e.data.Rate);
    this.editedEntities.set(e.data.ItemRateId, e.data);
  }

  getValueOrZero(value?: number) {
    if (value === undefined || value === null) {
      return 0;
    } else {
      return value;
    }
  }

  getLookupValueByIdAndLookupKey = (id, lookupKey) => {
    if (id === undefined || id === null) return '';
    let lookupValues = this.projectFieldLookups.find(l => l.ProjectFieldId === lookupKey);
    let lookup = lookupValues.LookupValues.find(l => l.value === id);
    if (lookup) {
      return lookup.text;
    }
    return '';
  }

  addColumn() {
    this.editMode = true;
    this.loading = true;
    let columns = this.gridApi.getColumnDefs();
    this.projectFieldLookups.push(this.addNewIProjectFieldDto);

    if (this.addNewIProjectFieldDto.ValueField !== null && this.addNewIProjectFieldDto.TextField !== null) {
      // dropdown
      let selectedProjectFieldId = this.addNewIProjectFieldDto.ProjectFieldId;
      let textFieldName = this.addNewIProjectFieldDto.TextField.toString();
      let valueFieldName = this.addNewIProjectFieldDto.ValueField.toString();
      this.itemRateManagementService.getProjectFieldLookup(this.addNewIProjectFieldDto.ProjectFieldId, this.IItemDto.FunctionId).subscribe(results => {
        let lookupValues = this.projectFieldLookups.find(l => l.ProjectFieldId === selectedProjectFieldId);
        lookupValues.LookupValues = Array.from(results).map(d => {
          return {
            value: d[valueFieldName].toString(),
            text: d[textFieldName].toString()
          };
        })
        columns.push(this.buildColumn(this.addNewIProjectFieldDto, selectedProjectFieldId, lookupValues)
        );

        this.gridApi.setGridOption('columnDefs', columns);
        this.IProjectFieldDtos = this.IProjectFieldDtos.filter(obj => obj.ProjectFieldId !== this.addNewIProjectFieldDto.ProjectFieldId);

        let newIProjectFieldToFieldXrefs = new Array<IProjectFieldToFieldXref>();
        let iProjectFieldToFieldXref = <IProjectFieldToFieldXref>{
          ProjectFieldId: this.addNewIProjectFieldDto.ProjectFieldId,
          ItemId: this.IItemDto.ItemId,
        };
        newIProjectFieldToFieldXrefs.push(iProjectFieldToFieldXref);

        this.itemRateManagementService.postItemProjectFields(newIProjectFieldToFieldXrefs).subscribe(data => {
          this.initNewIProjectFieldDto();
          this.showAddColumn = false;
          this.loading = false;
        });
      });
    }
    //number / string textbox
    else {
      columns.push(
        {
          field: this.addNewIProjectFieldDto.ProjectFieldId.toString(),
          headerName: this.addNewIProjectFieldDto.FieldName,
          headerTooltip: this.addNewIProjectFieldDto.PropertyPath,
          editable: true,
          cellEditorSelector: undefined
        }
      );

      this.gridApi.setGridOption('columnDefs', columns);
      this.IProjectFieldDtos = this.IProjectFieldDtos.filter(obj => obj.ProjectFieldId !== this.addNewIProjectFieldDto.ProjectFieldId);

      let newIProjectFieldToFieldXrefs = new Array<IProjectFieldToFieldXref>();
      let iProjectFieldToFieldXref = <IProjectFieldToFieldXref>{
        ProjectFieldId: this.addNewIProjectFieldDto.ProjectFieldId,
        ItemId: this.IItemDto.ItemId,
      };
      newIProjectFieldToFieldXrefs.push(iProjectFieldToFieldXref);

      this.itemRateManagementService.postItemProjectFields(newIProjectFieldToFieldXrefs).subscribe(data => {
        this.initNewIProjectFieldDto();
        this.showAddColumn = false;
        this.loading = false;
      });
    }
  }

  removeColumn() {
    this.dialogOpened = true;
  }

  removeColumnDefinition(iProjectFieldDto: IProjectFieldDto) {

    this.projectFieldLookups = this.projectFieldLookups.filter(p => p.ProjectFieldId !== iProjectFieldDto.ProjectFieldId);
    this.IProjectFieldDtos.push(iProjectFieldDto);
    let columns: ColDef[] = this.gridApi.getColumnDefs();
    columns = columns.filter(c => c.field !== 'Field_' + iProjectFieldDto.ProjectFieldId.toString());
    return columns;
  }

  private baseColumnDefs = null;
  addColumnDefinition(iProjectFieldDto: IProjectFieldDto) {
    if (this.projectFieldLookups.find(l => l.ProjectFieldId === iProjectFieldDto.ProjectFieldId)) {
      return;
    }

    let columns = this.gridApi.getColumnDefs();
    this.projectFieldLookups.push(iProjectFieldDto);

    if (iProjectFieldDto.ValueField !== null && iProjectFieldDto.TextField !== null) {
      let selectedProjectFieldId = iProjectFieldDto.ProjectFieldId;
      let textFieldName = iProjectFieldDto.TextField.toString();
      let valueFieldName = iProjectFieldDto.ValueField.toString();

      if (iProjectFieldDto.LookupValues !== null && iProjectFieldDto.LookupValues.length > 0) {
        let lookupValues = this.projectFieldLookups.find(l => l.ProjectFieldId === selectedProjectFieldId);
        lookupValues.LookupValues = Array.from(iProjectFieldDto.LookupValues).map(d => {
          return {
            value: d[valueFieldName].toString(),
            text: d[textFieldName].toString()
          };
        });

        columns.push(this.buildColumn(iProjectFieldDto, selectedProjectFieldId, lookupValues)
        );
        this.gridApi.setGridOption('columnDefs', columns);
        this.IProjectFieldDtos = this.IProjectFieldDtos.filter(obj => obj.ProjectFieldId !== iProjectFieldDto.ProjectFieldId);
      }
      else {
        this.itemRateManagementService.getProjectFieldLookup(iProjectFieldDto.ProjectFieldId, this.IItemDto.FunctionId).subscribe(results => {
          let lookupValues = this.projectFieldLookups.find(l => l.ProjectFieldId === selectedProjectFieldId);
          lookupValues.LookupValues = Array.from(results).map(d => {
            return {
              value: d[valueFieldName].toString(),
              text: d[textFieldName].toString()
            };
          })

          columns.push(this.buildColumn(iProjectFieldDto, selectedProjectFieldId, lookupValues)
          );
          this.gridApi.setGridOption('columnDefs', columns);
          this.IProjectFieldDtos = this.IProjectFieldDtos.filter(obj => obj.ProjectFieldId !== iProjectFieldDto.ProjectFieldId);
        });
      }
    }
    else {
      columns.push(
        {
          field: 'Field_' + iProjectFieldDto.ProjectFieldId.toString(),
          headerName: iProjectFieldDto.FieldName,
          headerTooltip: iProjectFieldDto.PropertyPath,
          editable: true,
          cellEditorSelector: undefined
        }
      );
      this.gridApi.setGridOption('columnDefs', columns);
      this.IProjectFieldDtos = this.IProjectFieldDtos.filter(obj => obj.ProjectFieldId !== iProjectFieldDto.ProjectFieldId);
    }
  }

  public addAuditColumns(): any {
    let columns: ColDef[] = this.gridApi.getColumnDefs();

    if (columns.filter(x => x.field == "Comments").length > 0)
      return;

    columns.push({
      field: 'Comments',
      editable: true,
      type: ['textColumn'],
    });
    columns.push({
      field: 'Created',
      editable: false,
      type: ['dateColumn'],
    });
    columns.push({
      field: 'CreatedBy',
      editable: false,
      type: ['textColumn'],
    });

    this.gridApi.setGridOption('columnDefs', columns);
  }

  public filterSettings: DropDownFilterSettings = {
    caseSensitive: false,
    operator: "contains",
  };

  private buildColumn(iProjectFieldDto: IProjectFieldDto, selectedProjectFieldId: number, lookupValues: IProjectFieldDto): any {
    return {
      field: 'Field_' + iProjectFieldDto.ProjectFieldId.toString(),
      headerName: `${iProjectFieldDto.FieldName} (${iProjectFieldDto.PropertyPathNames})`,
      headerTooltip: iProjectFieldDto.PropertyPath,
      editable: true,
      valueGetter: params => {
        let value = this.getLookupValueByIdAndLookupKey(params.data['Field_' + selectedProjectFieldId], selectedProjectFieldId);
        return value;
      },
      cellEditorSelector: params => {
        return {
          component: PxdAgGridSelectEditorComponent,
          params: {
            values: lookupValues.LookupValues
          }
        };
      },
      comparator: (valueA, valueB, nodeA, nodeB, isDescending) => {
        if (valueA == valueB) return 0;
        return (valueA > valueB) ? 1 : -1;
      }
    };
  }

  public confirmDoNotRemoveProperty() {
    this.showRemoveColumn = false;
    this.dialogOpened = false;
  }

  public confirmRemoveProperty() {
    let iProjectFieldToFieldXref = <IProjectFieldToFieldXref>{
      ProjectFieldId: this.removeIProjectFieldDto.ProjectFieldId,
      ItemId: this.IItemDto.ItemId
    };

    this.itemRateManagementService.deleteItemProjectField(iProjectFieldToFieldXref).subscribe(response => {
      this.transactAnyCurrentEditing();
      this.loading = true;
      let columns = this.removeColumnDefinition(this.removeIProjectFieldDto);
      this.initNewIProjectFieldDto();
      this.showRemoveColumn = false;
      this.dialogOpened = false;
      let rowData: Array<IItemRateDto> = Array<IItemRateDto>();
      this.gridApi.forEachNode(node => rowData.push(node.data));
      this.gridApi.resetColumnState();
      this.gridApi.setGridOption('columnDefs', columns);
      this.loading = false;
    });
  }

  //auto close popups
  //https://www.telerik.com/kendo-angular-ui/components/popup/closing/
  public readonly addColumnBtn = viewChild<ElementRef>("addColumnBtn");
  public readonly addColumnPopup = viewChild('addColumnPopup', { read: ElementRef });

  public readonly removeColumnBtn = viewChild<ElementRef>("removeColumnBtn");
  public readonly removeColumnPopup = viewChild('removeColumnPopup', { read: ElementRef });

  public keydown(event: any): void {
    if (event.keyCode === 27) {
      this.onToggleAddColumn(false);
      this.onToggleRemoveColumn(false);
    }
  }

  public documentClick(event: any): void {
    if (!this.contains(event.target)) {
      this.onToggleAddColumn(false);
      this.onToggleRemoveColumn(false);
    }
  }

  public onToggleAddColumn(show?: boolean): void {
    this.showAddColumn = show !== undefined ? show : !this.showAddColumn;
  }

  public onToggleRemoveColumn(show?: boolean): void {
    this.showRemoveColumn = show !== undefined ? show : !this.showRemoveColumn;
  }

  private contains(target: any): boolean {
    const addColumnPopup = this.addColumnPopup();
    const removeColumnPopup = this.removeColumnPopup();
    return (
      this.addColumnBtn().nativeElement.contains(target) ||
      (addColumnPopup ? addColumnPopup.nativeElement.contains(target) : false)
    )
      || (
        this.removeColumnBtn().nativeElement.contains(target) ||
        (removeColumnPopup ? removeColumnPopup.nativeElement.contains(target) : false)
      );
  }
}